import { createContext, useContext } from "react";
import { GfsAssessment } from "@/services/backend/gfs/assessment/types";
import {
  GfsCatalogItem,
  GfsConsumable,
} from "@/services/backend/gfs/catalog/types";
import { Pest } from "@/services/backend/pestcontrol/types/pest";
import { GfsAreaType } from "@/services/backend/gfs/assessment/components/gfs-site-selector-components";
import { PersistenceState } from "@/shared/lib/persistence-state/persistence-state";

export type AssessmentUpdateOptions = {
  areaId?: string;
  markPersistence?: boolean;
};

export type AssessmentEditorContextValue = {
  assessment: GfsAssessment;
  draft: GfsAssessment;
  areaScope: {
    buildingId: string;
    levelId: string;
    areaType: GfsAreaType;
  };
  areaPersistenceStates: Record<string, PersistenceState>;
  catalogItems: GfsCatalogItem[];
  catalogConsumables: GfsConsumable[];
  pests: Pest[];
  selectedPestIDs: string[];
  derivedPestIDs: Set<string>;
  accumulatedLaborDuration: number;
  globalLaborDuration: number;
  workTimeInputResetKey: number;
  setWorkTimeInputResetKey: (update: (key: number) => number) => void;
  setManualPestIDs: (nextManualIDs: string[]) => void;
  setAreaScope: (scope: {
    buildingId: string;
    levelId: string;
    areaType: GfsAreaType;
  }) => void;
  updateAssessment: (
    update:
      | Partial<GfsAssessment>
      | ((current: GfsAssessment) => Partial<GfsAssessment>),
    options?: AssessmentUpdateOptions,
  ) => void;
  persistCurrentBuildings: (
    nextBuildings: GfsAssessment["buildings"],
  ) => Promise<boolean>;
};

export const AssessmentEditorContext =
  createContext<AssessmentEditorContextValue | null>(null);

export function useAssessmentEditorContext() {
  const context = useContext(AssessmentEditorContext);
  if (!context) {
    throw new Error("GFS assessment editor context is required");
  }
  return context;
}
